import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getData, postData, putData, removeData } from "./useFetcher";
import { RootState, store } from "src/redux/store";
import { setState } from "src/redux/slices/state";

export enum QueryKeys {
  USER = "user",
  PROPERTIES = "properties",
  NOTIFICATION_HOME = "notification_home",
  MY_CONTRACTS = "my_contracts",
  CHARGES_HOME = "charges_home",
  BALANCE = "balance_financial",
  CHARGE_LOCATOR = "charge_locator",
  CHARGE_RENT = "charge_rent",
  TRANSACTION_BY_USER = "transaction_by_user",
}

interface UseServiceOptions {
  key?: QueryKeys;
  cacheMinutes?: number;
  refreshOnFocus?: boolean;
  manual?: boolean;
  fullUrl?: boolean;
  enabled?: boolean;
}

async function getRequest(url: string) {
  return fetch(url, { method: "GET" }).then((res) => res.json());
}

const useService = (url?: string, options?: UseServiceOptions) => {
  const {
    key,
    cacheMinutes,
    refreshOnFocus = false,
    manual = false,
    fullUrl = false,
    enabled = true,
  } = options || {};

  const dispatch = useDispatch();
  const cachedData = useSelector((state: any) =>
    key ? state.state[key] : null
  );

  // Estados para GET
  const [data, setData] = useState<any>(cachedData?.data || undefined);
  const [loading, setLoading] = useState<boolean>(key ? !cachedData : false);
  const [error, setError] = useState<any>(undefined);
  const hasFetchedOnce = useRef(false);
  const didMount = useRef(false);

  // Estados para POST
  const [postDataState, setPostDataState] = useState<any>(undefined);
  const [postLoading, setPostLoading] = useState<boolean>(false);
  const [postError, setPostError] = useState<any>(undefined);

  // Estados para PUT
  const [putDataState, setPutDataState] = useState<any>(undefined);
  const [putLoading, setPutLoading] = useState<boolean>(false);
  const [putError, setPutError] = useState<any>(undefined);

  // Estados para DELETE
  const [removeDataState, setRemoveDataState] = useState<any>(undefined);
  const [removeLoading, setRemoveLoading] = useState<boolean>(false);
  const [removeError, setRemoveError] = useState<any>(undefined);

  const logOptions = {
    url,
    method: "GET",
    key,
    cacheMinutes,
    refreshOnFocus,
    manual,
    fullUrl,
  };

  const fetchData = useCallback(
    async (force = false) => {
      if (!url || (manual && !force)) return;

      const lastUpdated = store.getState().state[`${key}_timestamp`] || 0;
      const isExpired = cacheMinutes
        ? Date.now() - lastUpdated > cacheMinutes * 60 * 1000
        : true;

      if (!force && key && cachedData && !isExpired) return;

      if (cachedData === null) setLoading(true);

      try {
        const response = fullUrl
          ? await getRequest(url)
          : await getData(url);

        setData(response);
        setError(null);

        if (key && !fullUrl) {
          dispatch(setState({ key, value: response }));
          dispatch(setState({ key: `${key}_timestamp`, value: Date.now() }));
        }
      } catch (err) {
        console.error("❌ [GET] Error fetching data:", { ...logOptions, error: err });
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [url, key, cachedData, dispatch, cacheMinutes, fullUrl, manual]
  );

  // Fetch inicial quando o hook é utilizado
  useEffect(() => {
    if (!url || manual || !enabled) return;
    if (!hasFetchedOnce.current) {
      hasFetchedOnce.current = true;
      fetchData();
    }
  }, [fetchData, manual, url, enabled]);

  // Refetch quando as dependências mudam (por exemplo, enabled)
  useEffect(() => {
    if (didMount.current) {
      if (hasFetchedOnce.current && enabled && !manual) {
        fetchData(true);
      }
    } else {
      didMount.current = true;
    }
  }, [enabled, manual, fetchData]);

  // Atualiza dados quando a aba (ou janela) volta a ficar visível
  useEffect(() => {
    if (!refreshOnFocus || manual || !url || !enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchData(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchData, refreshOnFocus, manual, url, enabled]);

  // Verifica periodicamente o cache e dispara refetch se estiver expirado
  useEffect(() => {
    if (!cacheMinutes || !key || manual || !url || !enabled) return;

    let timeout: NodeJS.Timeout;
    let isActive = document.visibilityState === "visible";

    const checkCache = () => {
      const lastUpdated = store.getState().state[`${key}_timestamp`] || 0;
      const isExpired = Date.now() - lastUpdated > cacheMinutes * 60 * 1000;

      if (isExpired && isActive && enabled) {
        fetchData(true);
      }

      timeout = setTimeout(checkCache, cacheMinutes * 60 * 1000);
    };

    checkCache();

    const handleVisibilityChange = () => {
      isActive = document.visibilityState === "visible";
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearTimeout(timeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [key, cacheMinutes, fetchData, manual, url, enabled]);

  // Função para POST
  const post = async (body: any) => {
    if (!url) return;
    setPostLoading(true);

    try {
      const response = await postData(url, { arg: body });
      setPostDataState(response);
      return { data: response, error: null };
    } catch (err) {
      setPostError(err);
      return { data: null, error: err };
    } finally {
      setPostLoading(false);
    }
  };

  // Função para PUT
  const put = async (body: any) => {
    if (!url) return;
    setPutLoading(true);

    try {
      const response = await putData(url, { arg: body });
      setPutDataState(response);
      return { data: response, error: null };
    } catch (err) {
      setPutError(err);
      return { data: null, error: err };
    } finally {
      setPutLoading(false);
    }
  };

  // Função para DELETE
  const remove = async (body: any) => {
    if (!url) return;
    setRemoveLoading(true);

    try {
      const response = await removeData(url, { arg: body });
      setRemoveDataState(response);
      return { data: response, error: null };
    } catch (err) {
      setRemoveError(err);
      return { data: null, error: err };
    } finally {
      setRemoveLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    refetch: () => url && fetchData(true),

    post,
    postData: postDataState,
    postLoading,
    postError,

    put,
    putData: putDataState,
    putLoading,
    putError,

    remove,
    removeData: removeDataState,
    removeLoading,
    removeError,
  };
};

export default useService;
